import React from 'react';
import { Sparkles, Activity, ChevronDown, Check, CircleCheck, ClockCheck } from 'lucide-react';
import { getSleepTime, updateSleepTime } from '../../utils/configureEventHandling';

const PRESETS = [
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hr" },
  { value: 120, label: "2 hr" },
  { value: 240, label: "4 hr" },
  { value: 480, label: "8 hr" },
  { value: 720, label: "12 hr" },
  { value: 1440, label: "24 hr" }
];

const formatIntervalLabel = (minutes) => {
  const m = Number(minutes);
  if (!m || m < 60) return `${m || 0} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} hr ${r} min` : `${h} hr`;
};

class DataRefreshIntervalPortlet extends React.Component {
    constructor(props) {
        super(props);
        const initialOption = localStorage.getItem('globalScanOption') || 'default';
        const initialInterval = parseInt(localStorage.getItem('globalScanInterval'), 10) || 30;

        this.state = {
            option: initialOption,
            intervalMin: initialInterval,
            isDirty: false,
            savedState: { option: initialOption, intervalMin: initialInterval },
            apiInfo: null,
            saveMessage: '',
            isSaving: false,
            isLoading: true,
            loadError: '',
        };
        this.handleOptionChange = this.handleOptionChange.bind(this);
        this.handleIntervalChange = this.handleIntervalChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.handleDiscard = this.handleDiscard.bind(this);
        this.loadSleepTime = this.loadSleepTime.bind(this);
    }

    componentDidMount() {
        this.loadSleepTime();

        if (window.location.hash === '#scan-interval-settings') {
            setTimeout(() => {
                const element = document.getElementById('scan-interval-settings');
                if (element) {
                    const yOffset = -95;
                    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
            }, 500);
        }
    }

    normalizeOption(raw) {
        const value = String(raw || '').trim().toLowerCase();
        if (['auto', 'autoscan', 'smart'].includes(value)) return 'auto';
        return 'default';
    }

    normalizeInterval(raw, fallback = 30) {
        const parsed = parseInt(raw, 10);
        if (Number.isNaN(parsed) || parsed <= 0) return fallback;
        return parsed;
    }

    parseApiResponse(response) {
        const option = this.normalizeOption(response.mode);
        const intervalMin = this.normalizeInterval(response.sleep_time, this.state.intervalMin);
        const scopeLabel = response.scope?.label
            || (response.scope?.dataset_count != null
                ? `All environments · ~${response.scope.dataset_count} datasets`
                : null);

        const apiInfo = {
            mode: option,
            sleep_time: intervalMin,
            interval_label: response.interval_label || formatIntervalLabel(intervalMin),
            scans_per_day: response.scans_per_day ?? Math.round(1440 / Math.max(1, intervalMin)),
            next_refresh: response.next_refresh || null,
            last_refresh: response.last_refresh || null,
            last_scan: response.last_scan || null,
            trigger: response.trigger || null,
            scope_label: scopeLabel,
        };

        return { option, intervalMin, apiInfo };
    }

    loadSleepTime(options = {}) {
        const quiet = !!options.quiet;
        if (!quiet) {
            this.setState({ isLoading: true, loadError: '' });
        } else {
            this.setState({ loadError: '' });
        }

        getSleepTime({
            tenant_id: Number(localStorage.getItem('team_id')),
        }).then((response) => {
            if (response?.code === 200) {
                const { option, intervalMin, apiInfo } = this.parseApiResponse(response);
                const savedState = { option, intervalMin };

                localStorage.setItem('globalScanOption', option);
                localStorage.setItem('globalScanInterval', String(intervalMin));

                this.setState({
                    option,
                    intervalMin,
                    savedState,
                    apiInfo,
                    isDirty: false,
                    isLoading: false,
                    loadError: '',
                });
                return;
            }

            this.setState({
                isLoading: false,
                loadError: response?.message || 'Failed to load scan interval',
            });
        }).catch(() => {
            this.setState({
                isLoading: false,
                loadError: 'Failed to load scan interval',
            });
        });
    }

    checkDirtyState(newState = this.state) {
        const { option, intervalMin, savedState } = newState;
        const isDirty = option !== savedState.option || Number(intervalMin) !== Number(savedState.intervalMin);
        this.setState({ isDirty });
    }

    handleOptionChange(option) {
        this.setState({ option }, () => this.checkDirtyState(this.state));
    }

    handleIntervalChange(e) {
        const intervalMin = parseInt(e.target.value, 10);
        this.setState({ intervalMin, option: 'default' }, () => this.checkDirtyState(this.state));
    }

    handleSave() {
        if (!this.state.isDirty || this.state.isSaving) return;

        const { option, intervalMin } = this.state;
        this.setState({ isSaving: true, saveMessage: '', loadError: '' });

        // PUT/POST body: { mode, sleep_interval }
        updateSleepTime({
            mode: option,
            sleep_interval: intervalMin,
        }).then((response) => {
            if (response?.code === 200) {
                localStorage.setItem('globalScanOption', option);
                localStorage.setItem('globalScanInterval', String(intervalMin));

                this.setState({
                    savedState: { option, intervalMin },
                    isDirty: false,
                    isSaving: false,
                    saveMessage: response.message || 'All changes saved',
                });

                // Refresh GET so info panel uses latest server values
                this.loadSleepTime({ quiet: true });

                setTimeout(() => {
                    this.setState((prev) => (
                        prev.saveMessage ? { saveMessage: '' } : null
                    ));
                }, 2000);
                return;
            }

            this.setState({
                isSaving: false,
                loadError: response?.message || 'Could not update scan interval',
            });
        }).catch(() => {
            this.setState({
                isSaving: false,
                loadError: 'Could not update scan interval',
            });
        });
    }

    handleDiscard() {
        this.setState({
            option: this.state.savedState.option,
            intervalMin: this.state.savedState.intervalMin,
            isDirty: false,
            loadError: '',
        });
    }

    getIntervalOptions() {
        const { intervalMin } = this.state;
        const hasPreset = PRESETS.some((p) => Number(p.value) === Number(intervalMin));
        if (hasPreset || !intervalMin) return PRESETS;
        return [{ value: intervalMin, label: formatIntervalLabel(intervalMin) }, ...PRESETS];
    }

    buildInfoItems() {
        const { option, intervalMin, apiInfo } = this.state;
        const scopeValue = apiInfo?.scope_label || 'All environments';

        if (option === 'auto') {
            return [
                { label: 'Trigger', value: apiInfo?.trigger || 'When data changes' },
                { label: 'Next refresh', value: apiInfo?.next_refresh || 'On data activity' },
                { label: 'Last scan', value: apiInfo?.last_scan || '—', colorClass: 'tw-text-emerald-600' },
                { label: 'Scope', value: scopeValue },
            ];
        }

        const intervalLabel = (apiInfo?.mode === 'default' && Number(apiInfo?.sleep_time) === Number(intervalMin) && apiInfo?.interval_label)
            ? apiInfo.interval_label
            : formatIntervalLabel(intervalMin);
        const scansPerDay = (apiInfo?.mode === 'default' && Number(apiInfo?.sleep_time) === Number(intervalMin) && apiInfo?.scans_per_day != null)
            ? apiInfo.scans_per_day
            : Math.round(1440 / Math.max(1, intervalMin));
        const nextRefresh = (apiInfo?.mode === 'default' && Number(apiInfo?.sleep_time) === Number(intervalMin) && apiInfo?.next_refresh)
            ? apiInfo.next_refresh
            : `in ~${formatIntervalLabel(Math.max(1, Math.round(intervalMin / 2)))}`;

        return [
            { label: 'Runs every', value: intervalLabel },
            { label: 'Frequency', value: `≈ ${scansPerDay} scans/day` },
            { label: 'Next refresh', value: nextRefresh },
            { label: 'Last refresh', value: apiInfo?.last_refresh || '—', colorClass: 'tw-text-emerald-600' },
            { label: 'Scope', value: scopeValue },
        ];
    }

    renderInfo() {
        const items = this.buildInfoItems();
        return items.map((stat, idx) => {
            const isLastOdd = idx === items.length - 1 && items.length % 2 !== 0;
            return (
                <React.Fragment key={idx}>
                    <div className={`tw-bg-[#fafbfc] tw-p-3 md:tw-p-0 md:tw-bg-transparent tw-flex tw-flex-col tw-gap-0.5 tw-shrink-0 ${isLastOdd ? 'tw-col-span-2 md:tw-col-auto' : ''}`}>
                        <span className="tw-text-[10px] tw-uppercase tw-tracking-[0.06em] tw-text-text-muted tw-font-semibold">{stat.label}</span>
                        <span className={`tw-text-[13px] tw-font-medium ${stat.colorClass || 'tw-text-text-heading'}`}>{stat.value}</span>
                    </div>
                    {idx < items.length - 1 && <span className="tw-hidden md:tw-block tw-w-px tw-h-7 tw-bg-[#E5E7EB] tw-shrink-0"></span>}
                </React.Fragment>
            );
        });
    }

    render() {
        const { option, intervalMin, isDirty, isSaving, saveMessage, isLoading, loadError, apiInfo } = this.state;
        const intervalOptions = this.getIntervalOptions();
        const isAuto = option === 'auto';
        const isDefault = option === 'default';

        const scansPerDay = (apiInfo?.mode === 'default' && Number(apiInfo?.sleep_time) === Number(intervalMin) && apiInfo?.scans_per_day != null)
            ? apiInfo.scans_per_day
            : Math.round(1440 / Math.max(1, intervalMin));

        return (
            <div id="scan-interval-settings" className='portlet pb-0'>
                <div className='portlet-title md:tw-flex tw-items-center tw-justify-between md:before:!tw-hidden md:after:!tw-hidden'>
                    <div className='caption'>
                        <span className="text-uppercase font-dark dark:!tw-text-white">Data refresh interval</span>
                        <p className="tw-text-[12px] tw-text-text-muted dark:tw-text-[#B2B5BE] tw-mt-0.5 tw-leading-snug tw-m-0">How often Qualdo scans your datasets across all environments.</p>
                    </div>
                </div>
                <div className='portlet-body !tw-p-0'>
                    {isLoading ? (
                        <div className="tw-p-6 tw-text-[13px] tw-text-text-muted dark:tw-text-[#B2B5BE]">Loading scan interval…</div>
                    ) : (
                        <>
                            <div className="tw-p-4">
                                <div role="radiogroup" aria-label="Refresh mode" className="tw-grid sm:tw-grid-cols-2 tw-gap-3">
                                    <div
                                        role="radio"
                                        tabIndex="0"
                                        aria-checked={isDefault}
                                        onClick={() => this.handleOptionChange('default')}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.handleOptionChange('default'); } }}
                                        className={`qd-card tw-cursor-pointer tw-rounded-xl tw-border tw-border-solid tw-p-4 tw-outline-none tw-transition-all tw-duration-300 tw-ease-in-out ${isDefault ? 'tw-border-link-blue dark:tw-border-indigo-500/70 tw-bg-link-blue/[0.04] dark:tw-bg-indigo-500/15 tw-shadow-sm focus-visible:tw-ring-2 focus-visible:tw-ring-link-blue/30' : 'tw-border-[#E5E7EB] dark:tw-border-white/10 tw-bg-white dark:tw-bg-[#1E1E2D] hover:-tw-translate-y-1 hover:tw-shadow-md hover:dark:tw-bg-[#252538]'}`}
                                    >
                                        <div className="tw-flex tw-items-center tw-gap-2.5">
                                            <span className={`qd-card-ic tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-shrink-0 ${isDefault ? 'tw-bg-link-blue/12 dark:tw-bg-indigo-500/25 tw-text-link-blue dark:tw-text-indigo-400' : 'tw-bg-slate-100 dark:tw-bg-white/10 tw-text-text-muted dark:tw-text-[#B2B5BE]'}`}>
                                                <ClockCheck className="tw-w-[18px] tw-h-[18px]" />
                                            </span>
                                            <span className="tw-text-[14px] tw-font-semibold tw-text-text-heading dark:tw-text-white">Default interval</span>
                                            <span className="tw-text-[9.5px] tw-font-semibold tw-uppercase tw-tracking-wide tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-slate-100 dark:tw-bg-white/10 tw-text-text-muted dark:tw-text-[#B2B5BE]">Predictable</span>
                                            <span className={`qd-card-radio tw-ml-auto tw-w-[18px] tw-h-[18px] tw-rounded-full tw-shrink-0 tw-flex tw-items-center tw-justify-center ${isDefault ? 'tw-bg-link-blue dark:tw-bg-indigo-500 tw-text-white tw-border-0' : 'tw-border tw-border-solid tw-border-slate-300 dark:tw-border-slate-600'}`}>
                                                {isDefault && <Check className="tw-w-3 tw-h-3" />}
                                            </span>
                                        </div>
                                        <p className="tw-text-[12px] tw-text-text-muted dark:tw-text-[#B2B5BE] !tw-mt-2 tw-leading-snug tw-mb-0">Scans <strong className="tw-font-bold tw-text-text-heading dark:tw-text-white">all environments</strong> on a fixed schedule you set — steady and predictable.</p>
                                        <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                                            <span className="tw-text-[12.5px] tw-text-text-muted dark:tw-text-[#B2B5BE]">Runs every</span>
                                            <div className="tw-relative">
                                                <select
                                                    value={intervalMin}
                                                    onChange={this.handleIntervalChange}
                                                    onClick={(e) => e.stopPropagation()}
                                                    aria-label="Refresh interval"
                                                    className="tw-appearance-none tw-rounded-lg tw-border tw-border-solid tw-border-[#E5E7EB] dark:tw-border-white/15 tw-bg-white dark:tw-bg-[#252538] tw-pl-3 tw-pr-8 tw-py-1.5 tw-text-[14px] tw-font-semibold tw-text-link-blue dark:tw-text-indigo-400 tw-outline-none focus:tw-border-link-blue focus:tw-ring-2 focus:tw-ring-link-blue/15 tw-cursor-pointer"
                                                >
                                                    {intervalOptions.map((p) => (
                                                        <option key={p.value} value={p.value} className="dark:tw-bg-[#1E1E2D] dark:tw-text-white">{p.label}</option>
                                                    ))}
                                                </select>
                                                <ChevronDown className="tw-w-4 tw-h-4 tw-text-text-muted dark:tw-text-slate-400 tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-pointer-events-none" />
                                            </div>
                                            <span className="tw-text-[12px] tw-text-text-muted dark:tw-text-[#B2B5BE]">≈ <span className="tw-font-semibold tw-text-text-body dark:tw-text-white">{scansPerDay} scans</span>/day</span>
                                        </div>
                                    </div>

                                    <div
                                        role="radio"
                                        tabIndex="0"
                                        aria-checked={isAuto}
                                        onClick={() => this.handleOptionChange('auto')}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.handleOptionChange('auto'); } }}
                                        className={`qd-card tw-cursor-pointer tw-rounded-xl tw-border tw-border-solid tw-p-4 tw-outline-none tw-transition-all tw-duration-300 tw-ease-in-out ${isAuto ? 'tw-border-emerald-300 dark:tw-border-emerald-500/70 tw-bg-emerald-50/50 dark:tw-bg-emerald-500/15 tw-shadow-sm focus-visible:tw-ring-2 focus-visible:tw-ring-emerald-400/40' : 'tw-border-[#E5E7EB] dark:tw-border-white/10 tw-bg-white dark:tw-bg-[#1E1E2D] hover:-tw-translate-y-1 hover:tw-shadow-md hover:dark:tw-bg-[#252538]'}`}
                                    >
                                        <div className="tw-flex tw-items-center tw-gap-2.5">
                                            <span className={`qd-card-ic tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-shrink-0 ${isAuto ? 'tw-bg-emerald-100 dark:tw-bg-emerald-500/25 tw-text-emerald-600 dark:tw-text-emerald-400' : 'tw-bg-slate-100 dark:tw-bg-white/10 tw-text-text-muted dark:tw-text-[#B2B5BE]'}`}>
                                                <Sparkles className="tw-w-[18px] tw-h-[18px]" />
                                            </span>
                                            <span className="tw-text-[14px] tw-font-semibold tw-text-text-heading dark:tw-text-white">Auto scan</span>
                                            <span className="tw-text-[9.5px] tw-font-semibold tw-uppercase tw-tracking-wide tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-emerald-50 dark:tw-bg-emerald-500/20 tw-text-emerald-600 dark:tw-text-emerald-400">Smart</span>
                                            <span className={`qd-card-radio tw-ml-auto tw-w-[18px] tw-h-[18px] tw-rounded-full tw-shrink-0 tw-flex tw-items-center tw-justify-center ${isAuto ? 'tw-bg-emerald-500 dark:tw-bg-emerald-500 tw-text-white tw-border-0' : 'tw-border tw-border-solid tw-border-slate-300 dark:tw-border-slate-600'}`}>
                                                {isAuto && <Check className="tw-w-3 tw-h-3" />}
                                            </span>
                                        </div>
                                        <p className="tw-text-[12px] tw-text-text-muted dark:tw-text-[#B2B5BE] !tw-mt-2 tw-leading-snug tw-mb-0">Qualdo scans <strong className="tw-font-bold tw-text-text-heading dark:tw-text-white">all environments</strong> automatically when it detects new or refreshed data.</p>
                                        <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-text-[12.5px] tw-text-text-muted dark:tw-text-[#B2B5BE]">
                                            <Activity className="tw-w-4 tw-h-4 tw-shrink-0" />No timer to set — always fresh, variable load.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="tw-px-4 tw-pb-4 md:tw-p-4 d-none">
                                <div className="tw-rounded-xl tw-border tw-border-solid tw-border-[#E5E7EB] dark:tw-border-white/10 tw-bg-[#E5E7EB] md:tw-bg-[#fafbfc] dark:md:tw-bg-[#1E1E2D] tw-overflow-hidden tw-grid tw-grid-cols-2 tw-gap-px md:tw-flex md:tw-flex-wrap md:tw-items-center md:tw-gap-x-5 md:tw-gap-y-3 md:tw-px-4 md:tw-py-3">
                                    {this.renderInfo()}
                                </div>
                            </div>
                        </>
                    )}

                    <div className='tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-0 tw-border-t tw-border-solid tw-border-[#E5E7EB] dark:tw-border-white/10 tw-flex-wrap'>
                        <div className="tw-flex tw-items-center tw-gap-2.5 tw-text-[12.5px] tw-min-w-0">
                            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-font-medium tw-shrink-0">
                                {loadError ? <span className="tw-text-[#DC2626]">{loadError}</span> : null}
                                {isDirty ? (
                                    <>
                                        <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-orange tw-shrink-0"></span>
                                        <span className="tw-text-text-body dark:tw-text-white">Unsaved changes</span>
                                    </>
                                ) : null}
                                {saveMessage ? (
                                    <>
                                        <CircleCheck className="tw-w-4 tw-h-4 tw-text-emerald-500" />
                                        <span className="tw-text-text-body dark:tw-text-white">{saveMessage}</span>
                                    </>
                                ) : null}
                            </span>
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                            {isDirty && (
                                <button
                                    type="button"
                                    onClick={this.handleDiscard}
                                    className="tw-text-[13px] tw-font-medium tw-text-text-muted dark:tw-text-slate-400 hover:tw-text-text-body dark:hover:tw-text-white hover:tw-bg-slate-50 dark:hover:tw-bg-white/5 tw-transition-colors tw-px-3.5 tw-py-2 tw-rounded-lg tw-border-0 tw-bg-transparent tw-cursor-pointer"
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                type="button"
                                disabled={!isDirty || isSaving || isLoading}
                                onClick={this.handleSave}
                                className={`tw-text-white tw-border-0 tw-cursor-pointer tw-rounded-lg tw-px-5 tw-py-2 tw-text-[13px] tw-font-semibold tw-transition-colors disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-inline-flex tw-items-center tw-gap-2 tw-min-w-[124px] tw-justify-center tw-shadow-sm ${isSaving ? 'tw-bg-[#1f8a4c]' : 'tw-bg-orange hover:tw-bg-[#DC2626]'}`}
                            >
                                <Check className="tw-w-4 tw-h-4" />
                                <span>{isSaving ? 'Saving...' : 'Save'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default DataRefreshIntervalPortlet;
